import { Injectable } from "@angular/core";
import { ContentValue } from "@fabric-types/svc-content";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { GtmRegistrationType } from "@obg/gtm.types/adaptive";
import { ActionWithPayload } from "@obg/ngrx-utils";
import { AffiliateService, CommonState } from "@obg/ngx.common";
import { JurisdictionSelector } from "@obg/ngx.core";
import { DocumentRef, TelemetryService } from "@obg/ngx.global.evo";
import { Observable } from "rxjs";
import { filter, map, mergeMap, take, tap, withLatestFrom } from "rxjs/operators";

import { SEAMLESS_REGISTRATION_ACTION_TYPE } from "../../seamless-registration/seamless-registration.action";
import {
	CustomerRegistrationStatus,
	SeamlessRegistrationStatus,
} from "../../seamless-registration/seamless-registration.model";
import { SeamlessRegistrationSelector } from "../../seamless-registration/seamless-registration.selector";
import { RegistrationFeatureState } from "../../state";

import { SeamlessRegistrationTelemetryEventName } from "./seamless-registration-telemetry.model";

@Injectable()
export class SeamlessRegistrationTelemetryEffect {
	seamlessRegistrationFail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationStateSuccess),
				map((x: ActionWithPayload) => x.payload),
				filter(
					({ status, customerRegistrationStatus }) =>
						status === SeamlessRegistrationStatus.Failed ||
						customerRegistrationStatus === CustomerRegistrationStatus.Failed ||
						customerRegistrationStatus === CustomerRegistrationStatus.BlockedByIovation
				),
				map(({ status, customerRegistrationStatus }) =>
					status === SeamlessRegistrationStatus.Failed ? status : customerRegistrationStatus
				),
				tap((status) =>
					this.telemetryService.track({
						event: SeamlessRegistrationTelemetryEventName.FailedRegistration,
						Error: status,
					})
				)
			),
		{ dispatch: false }
	);

	seamlessRegistrationSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(SEAMLESS_REGISTRATION_ACTION_TYPE.getRegistrationStateSuccess),
				map((x: ActionWithPayload) => x.payload),
				filter(
					(response) =>
						response.customerRegistrationStatus === CustomerRegistrationStatus.Completed ||
						response.customerRegistrationStatus === CustomerRegistrationStatus.EmailConfirmation
				),
				withLatestFrom(this.store.select(this.seamlessRegistrationSelector.getState())),
				map(([, x]) => x),
				filter((registrationState) => !!registrationState.country),
				mergeMap((registrationState) =>
					new Observable(
						this.documentRef.obgEventBus.retrieve(ContentValue.getCountryByCode, {
							countryCode: registrationState.country!,
						})
					).pipe(
						take(1),
						withLatestFrom(this.store.select(JurisdictionSelector.get)),
						tap(([country, jurisdiction]) =>
							this.telemetryService.track({
								event: SeamlessRegistrationTelemetryEventName.ConfirmedRegistration,
								GUID: registrationState.customerReference,
								HashedEmail: registrationState.hashedEmail || "Missing",
								Age: "",
								Sex: "",
								Location: country?.name,
								PartnerID: this.affiliateService.getCookieName(),
								RegistrationType: jurisdiction,
								RegistrationMethod: GtmRegistrationType.Regular,
							})
						)
					)
				)
			),
		{ dispatch: false }
	);

	constructor(
		private actions$: Actions,
		private telemetryService: TelemetryService,
		private store: Store<RegistrationFeatureState & CommonState>,
		private affiliateService: AffiliateService,
		private seamlessRegistrationSelector: SeamlessRegistrationSelector,
		private documentRef: DocumentRef
	) {}
}
